import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  GetJobResumeAnalysisBase,
  JobByIdResponse,
  JobNotesResponse,
} from 'src/openapi';
import type { RootState } from 'src/redux/index';
import { bulkStatusUpdate } from 'src/redux/actions/applicantReview';
import type {
  AnalyseSocketResponse,
  BulkAnalysisFilter,
  BulkResumeApplicantState,
} from '../types/xray';
import {
  getCurrentAnalysedResume,
  updateRating,
  getAnalysedResumeList,
  updateHiringStatus,
  fetchAllBulkApplicantTags,
  fetchGptPostedJobList,
} from '../actions/xray';

export const bulkAnalysisFilterInitialState: BulkAnalysisFilter = {
  minAnalysisSkillScore: undefined,
  maxAnalysisSkillScore: undefined,
  minAnalysisOverallScore: undefined,
  maxAnalysisOverallScore: undefined,
  minAnalysisExperienceScore: undefined,
  maxAnalysisExperienceScore: undefined,
  minRating: undefined,
  maxRating: undefined,
  matchingSkills: undefined,
  missingSkills: undefined,
  applicantLocation: undefined,
  applicantTags: undefined,
  start_date: undefined,
  end_date: undefined,
  analysisStatus: undefined,
  sortBy: 'resume_analysis_overall_score',
  sortOrder: 'desc',
};

const initialState: BulkResumeApplicantState = {
  fetchingCurrentApplicantData: false,
  currentResumeIndex: undefined,
  finalResumeAnalyseList: undefined,
  currentApplicant: undefined,
  clickedApplicant: undefined,
  jobData: undefined,
  bulkAnalysisCurrentFilters: bulkAnalysisFilterInitialState,
  alreadyExistIds: [],
  selectAll: false,
  selectedApplicant: [],
  searchQuery: '',
  hiringStatus: '',
  allApplicantTags: [],
  isProcessingBatch: false,
  jobList: null,
};
/* eslint-disable no-param-reassign */
const BulkResumeApplicantSlice = createSlice({
  name: 'BulkResumeApplicant',
  initialState,
  reducers: {
    setJobData: (state, action: PayloadAction<JobByIdResponse>) => {
      state.jobData = action.payload;
    },

    resetResumeIndex: (state) => {
      state.currentResumeIndex = undefined;
    },
    updateApplicantAnalyseData: (
      state,
      action: PayloadAction<AnalyseSocketResponse>,
    ) => {
      if (
        state.clickedApplicant?.id &&
        action?.payload?.analysis_id &&
        state.clickedApplicant.id === action.payload.analysis_id
      ) {
        state.clickedApplicant.first_name = action.payload.first_name;
        state.clickedApplicant.last_name = action.payload.last_name;
        state.clickedApplicant.overall_score = action.payload.overall_score;
        state.clickedApplicant.status = action.payload.status;
      }

      if (state.finalResumeAnalyseList?.all_resume_analysis) {
        state.finalResumeAnalyseList.all_resume_analysis =
          state.finalResumeAnalyseList.all_resume_analysis.map((val) => {
            if (
              val.id === action.payload.analysis_id &&
              val.status === 'Processing'
            ) {
              val.first_name = action.payload.first_name;
              val.last_name = action.payload.last_name;
              val.overall_score = action.payload.overall_score;
              val.status = action.payload.status;
            }
            return val;
          });
      }
    },
    setBulkAnalysisCurrentFilters: (
      state,
      action: PayloadAction<BulkAnalysisFilter>,
    ) => {
      state.bulkAnalysisCurrentFilters = action.payload;
    },
    resetCurrentUser: (state) => {
      state.currentApplicant = undefined;
      state.currentResumeIndex = undefined;
      state.clickedApplicant = undefined;
    },
    resetData: (state) => {
      // Do not reset filters here. Otherwise useEffect in LeftResumeCard.tsx will run again and all applicants will be
      // fetched again.
      state.currentApplicant = undefined;
      state.currentResumeIndex = undefined;
      state.clickedApplicant = undefined;
      state.finalResumeAnalyseList = undefined;
      state.selectAll = false;
      state.selectedApplicant = [];
      state.jobData = undefined;
      state.alreadyExistIds = [];
    },
    resetFilter: (state) => {
      state.bulkAnalysisCurrentFilters =
        initialState.bulkAnalysisCurrentFilters;
    },
    setIsProcessingBatch: (state, action: PayloadAction<boolean>) => {
      state.isProcessingBatch = action.payload;
    },
    setCurrentResumeIndex: (state, action: PayloadAction<number>) => {
      state.currentResumeIndex = action.payload;
      if (
        (state.finalResumeAnalyseList?.all_resume_analysis?.length || 0) >
        action.payload
      ) {
        state.clickedApplicant =
          state.finalResumeAnalyseList?.all_resume_analysis?.[action.payload];
      }
    },
    setClickedApplicant: (
      state,
      action: PayloadAction<GetJobResumeAnalysisBase>,
    ) => {
      state.clickedApplicant = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    selectApplicant: (
      state,
      action: PayloadAction<GetJobResumeAnalysisBase>,
    ) => {
      state.selectedApplicant.push(action.payload);
    },
    deselectApplicant: (
      state,
      action: PayloadAction<GetJobResumeAnalysisBase>,
    ) => {
      state.selectedApplicant = state.selectedApplicant.filter(
        (applicant) => applicant.id !== action.payload.id,
      );
    },
    toggleSelectAll: (state) => {
      state.selectAll = !state.selectAll;
    },
    deselectAll: (state) => {
      state.selectedApplicant = [];
      state.selectAll = false;
    },
    addToAlreadyExists: (state, action: PayloadAction<string>) => {
      if (!state.alreadyExistIds.includes(action.payload)) {
        state.alreadyExistIds = [...state.alreadyExistIds, action.payload];
      }
    },
    removeFromAlreadyExists: (state, action: PayloadAction<string>) => {
      state.alreadyExistIds = state.alreadyExistIds.filter(
        (val) => val !== action.payload,
      );
    },
    setHiringStaus: (state, action: PayloadAction<string>) => {
      state.hiringStatus = action.payload;
    },
    resetAlreadyExists: (state) => {
      state.alreadyExistIds = [];
    },
    updateBulkStatus: (
      state,
      action: PayloadAction<{
        isSelectedAll?: boolean;
        applicantIds?: string[];
        status: string;
      }>,
    ) => {
      if (!state.finalResumeAnalyseList?.all_resume_analysis) {
        return;
      }

      if (action.payload.isSelectedAll) {
        state.finalResumeAnalyseList.all_resume_analysis =
          state.finalResumeAnalyseList.all_resume_analysis.map((val) => ({
            ...val,
            hiring_status: action.payload.status,
          }));
      } else {
        state.finalResumeAnalyseList.all_resume_analysis =
          state.finalResumeAnalyseList.all_resume_analysis.map((val) => {
            if (action.payload.applicantIds?.includes(val.id ?? '')) {
              return {
                ...val,
                hiring_status: action.payload.status,
              };
            }
            return val;
          });
      }
    },
    updateCurrentBulkApplicantNotes: (
      state,
      action: PayloadAction<JobNotesResponse[]>,
    ) => {
      state.currentApplicant = {
        ...state.currentApplicant,
        user_notes: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAnalysedResumeList.fulfilled, (state, action) => {
      if (
        action.meta.arg.countOnly ||
        (state.finalResumeAnalyseList?.page === action.payload.page &&
          action.payload.page !== 1)
      ) {
        return;
      }

      if (action.meta.arg.page === 1) {
        state.finalResumeAnalyseList = action.payload;
      } else if (
        state.finalResumeAnalyseList?.all_resume_analysis &&
        action.payload.all_resume_analysis
      ) {
        state.finalResumeAnalyseList.all_resume_analysis = [
          ...state.finalResumeAnalyseList.all_resume_analysis,
          ...action.payload.all_resume_analysis,
        ];
        state.finalResumeAnalyseList.page = action.payload.page;
        state.finalResumeAnalyseList.total_pages = action.payload.total_pages;
      }
    });
    builder.addCase(getCurrentAnalysedResume.pending, (state) => {
      state.fetchingCurrentApplicantData = true;
    });
    builder.addCase(fetchGptPostedJobList.fulfilled, (state, action) => {
      if (action.payload.page_no === 1) {
        state.jobList = action.payload;
      } else if (state.jobList) {
        state.jobList.jobs = [
          ...(state.jobList?.jobs ?? []),
          ...(action.payload.jobs ?? []),
        ];
        state.jobList.page_no = action.payload.page_no;
        state.jobList.page_size = action.payload.page_size;
        state.jobList.total_pages = action.payload.total_pages;
        state.jobList.total_jobs = action.payload.total_jobs;
        state.jobList.total_unfiltered_jobs =
          action.payload.total_unfiltered_jobs;
      }
    });
    builder.addCase(getCurrentAnalysedResume.fulfilled, (state, action) => {
      state.currentApplicant = action.payload;
      state.fetchingCurrentApplicantData = false;
      if (
        state.clickedApplicant?.status !== action.payload.status &&
        state.finalResumeAnalyseList?.all_resume_analysis
      ) {
        state.finalResumeAnalyseList.all_resume_analysis =
          state.finalResumeAnalyseList.all_resume_analysis.map((val) => {
            if (val.id === action.meta.arg.analysis_id) {
              val.email = action.payload.email;
              val.first_name = action.payload.first_name;
              val.last_name = action.payload.last_name;
              val.overall_score = action.payload.response?.overall?.score ?? 0;
              val.hiring_status = action.payload.hiring_status;
              val.status = action.payload.status;
              val.phone_number = action.payload.phone_number;
              val.rating = action.payload.rating;
            }
            return val;
          });
      }
    });
    builder.addCase(updateHiringStatus.fulfilled, (state, action) => {
      if (state.finalResumeAnalyseList?.all_resume_analysis) {
        state.finalResumeAnalyseList.all_resume_analysis =
          state.finalResumeAnalyseList.all_resume_analysis.map((val) => {
            if (val.id === action.meta.arg.applicantId) {
              val.hiring_status = action.meta.arg.obj.hiring_status;
            }
            return val;
          });
      }

      if (state.clickedApplicant) {
        state.clickedApplicant.hiring_status =
          action.meta.arg.obj.hiring_status;
      }
    });
    builder.addCase(bulkStatusUpdate.fulfilled, (state, action) => {
      if (state.finalResumeAnalyseList?.all_resume_analysis) {
        state.finalResumeAnalyseList.all_resume_analysis =
          state.finalResumeAnalyseList.all_resume_analysis.map((val: any) => {
            if (
              action.meta.arg.payload.custom_applicant_user_ids?.includes(
                val.id,
              )
            ) {
              val.hiring_status = action.meta.arg.payload.hiring_status;
            }
            return val;
          });
      }

      if (state.clickedApplicant) {
        state.clickedApplicant.hiring_status =
          action.meta.arg.payload.hiring_status;
      }
    });
    builder.addCase(updateRating.fulfilled, (state, action) => {
      if (state.finalResumeAnalyseList?.all_resume_analysis) {
        state.finalResumeAnalyseList.all_resume_analysis =
          state.finalResumeAnalyseList?.all_resume_analysis?.map((val) => {
            if (val.id === action.meta.arg.applicantId) {
              if (action.meta.arg.ratingValue === 0) {
                val.rating = undefined;
              } else {
                val.rating = Number(action.meta.arg.ratingValue);
              }
            }

            return val;
          });
      }

      if (
        state.clickedApplicant &&
        state.clickedApplicant.id === action.meta.arg.applicantId
      ) {
        state.clickedApplicant.rating = Number(action.meta.arg.ratingValue);
      }
    });
    builder.addCase(fetchAllBulkApplicantTags.fulfilled, (state, action) => {
      state.allApplicantTags = action.payload.applicant_tag_list ?? [];
    });
    // builder.addCase(getAnalysedResume.fulfilled, (state, action) => {});
  },
});
/* eslint-enable no-param-reassign */

const { reducer, actions } = BulkResumeApplicantSlice;

export default reducer;
export const resumeApplicantSelector = (state: RootState) =>
  state.bulkResumeApplicant;
export const {
  resetResumeIndex,
  resetCurrentUser,
  resetData,
  setBulkAnalysisCurrentFilters,
  setJobData,
  resetFilter,
  updateApplicantAnalyseData,
  setCurrentResumeIndex,
  setSearchQuery,
  selectApplicant,
  deselectAll,
  deselectApplicant,
  toggleSelectAll,
  updateBulkStatus,
  updateCurrentBulkApplicantNotes,
  addToAlreadyExists,
  removeFromAlreadyExists,
  resetAlreadyExists,
  setClickedApplicant,
  setHiringStaus,
  setIsProcessingBatch,
} = actions;
